import React, { useEffect, useState } from 'react';
import { commsService } from '../services/commsService';

/**
 * Threaded comments on clinical artifacts (notes, reports, chart facts, etc.)
 */
const CommentThread = ({ targetType, targetId, patientId }) => {
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!targetType || !targetId) return;
    try {
      setLoading(true);
      const res = await commsService.listComments(targetType, targetId);
      setComments(res.data || []);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [targetType, targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    try {
      setSaving(true);
      await commsService.addComment({
        targetType,
        targetId,
        patientId,
        body: body.trim(),
      });
      setBody('');
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to add comment');
    } finally {
      setSaving(false);
    }
  };

  const resolve = async (id) => {
    try {
      await commsService.resolveComment(id);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to resolve');
    }
  };

  return (
    <div className="mt-3 border-t border-gray-100 pt-3">
      <div className="text-xs font-medium text-gray-500 mb-2">Comments</div>
      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-2 mb-2 max-h-40 overflow-y-auto">
          {comments.length === 0 && (
            <p className="text-xs text-gray-400">No comments yet.</p>
          )}
          {comments.map((c) => (
            <div
              key={c._id}
              className={`text-xs rounded p-2 ${
                c.isResolved ? 'bg-gray-50 text-gray-400' : 'bg-blue-50'
              }`}
            >
              <div className="flex justify-between gap-2">
                <span className="font-medium">
                  {c.authorUserId?.name || c.authorUserId?.email || 'User'}
                </span>
                <span className="text-gray-400">
                  {c.createdAt ? new Date(c.createdAt).toLocaleString() : ''}
                </span>
              </div>
              <p className="whitespace-pre-wrap mt-1">{c.body}</p>
              {!c.isResolved && (
                <button
                  type="button"
                  className="text-primary underline mt-1"
                  onClick={() => resolve(c._id)}
                >
                  Resolve
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <form onSubmit={submit} className="flex gap-2">
        <input
          className="flex-1 border rounded px-2 py-1 text-xs"
          placeholder="Add a comment…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <button
          type="submit"
          disabled={saving || !body.trim()}
          className="text-xs bg-primary text-white px-2 py-1 rounded disabled:opacity-50"
        >
          Post
        </button>
      </form>
    </div>
  );
};

export default CommentThread;
